import { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { recordLead } from '../../lib/leads';
import { toast } from 'sonner';

export default function EnquiryModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: ''
  });

  useEffect(() => {
    // If they already submitted in this session, do not show the popup again
    if (sessionStorage.getItem('enquired') === 'true') {
      return;
    }

    const intervalTime = 150000; // 2.5 minutes (150 seconds)
    
    // Set a recurring timer to show the popup
    const timer = setInterval(() => {
      if (sessionStorage.getItem('enquired') !== 'true') {
        setIsOpen(true);
      }
    }, intervalTime);

    return () => clearInterval(timer);
  }, []);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await recordLead({
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        source: 'Periodic Enquiry Popup',
        status: 'New'
      });
      
      // Persist the state in sessionStorage so they aren't repeatedly prompted after a successful submission
      sessionStorage.setItem('enquired', 'true');
      toast.success('Thank you! Our property experts will contact you shortly.');
      setFormData({ name: '', phone: '', email: '' });
      setIsOpen(false);
    } catch (err) {
      toast.error('Failed to submit enquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
          {/* Backdrop with elegant glass blur */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm"
          />

          {/* Modal Container matches the user design perfectly */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="relative bg-white rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl p-6 md:p-10 border border-gray-100 z-10"
          >
            {/* Close Button top right */}
            <button 
              onClick={handleClose}
              className="absolute top-6 right-6 p-1.5 hover:bg-gray-50 rounded-full transition-colors text-gray-400 hover:text-gray-900"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Modal Header */}
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 font-manrope">
                Enquire
              </h2>
            </div>

            {/* Enquiry Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label className="block text-xs font-black uppercase text-gray-400 tracking-wider ml-1">
                  Name
                </label>
                <input 
                  required
                  type="text" 
                  className="w-full bg-white border border-gray-200 focus:border-[#24477e] focus:ring-2 focus:ring-[#24477e]/10 outline-none rounded-xl px-4 py-3.5 text-sm font-semibold transition-all text-gray-700"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <label className="block text-xs font-black uppercase text-gray-400 tracking-wider ml-1">
                  Number
                </label>
                <input 
                  required
                  type="tel" 
                  className="w-full bg-white border border-gray-200 focus:border-[#24477e] focus:ring-2 focus:ring-[#24477e]/10 outline-none rounded-xl px-4 py-3.5 text-sm font-semibold transition-all text-gray-700"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <label className="block text-xs font-black uppercase text-gray-400 tracking-wider ml-1">
                  Email
                </label>
                <input 
                  required
                  type="email" 
                  className="w-full bg-white border border-gray-200 focus:border-[#24477e] focus:ring-2 focus:ring-[#24477e]/10 outline-none rounded-xl px-4 py-3.5 text-sm font-semibold transition-all text-gray-700"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>

              {/* Submit Button */}
              <button 
                type="submit" 
                disabled={loading}
                className="w-full bg-black text-white rounded-xl py-4 font-black uppercase tracking-widest text-xs flex items-center justify-center gap-2 hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-75 disabled:active:scale-100 mt-2 shadow-lg shadow-black/10"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Submit'
                )}
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
